import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowUpRight, Play } from "lucide-react";
import { useCalmMotion } from "@/lib/studio";
import { cn } from "@/lib/utils";

const eyebrow = (p) => (p.kind === "video" ? "Video · Coming soon" : p.operation === "edit" ? "Edit with one sentence" : "Image preset");

function Slide({ preset, active, onOpen, index }) {
  return (
    <div className="min-w-0 shrink-0 grow-0 basis-[90%] pl-3 sm:basis-[84%] lg:basis-[74%]" data-testid={`carousel-slide-${index}`}>
      <button type="button" onClick={() => onOpen(preset)} aria-label={`Open preset ${preset.title}`}
        className={cn("group relative block h-[min(58vh,430px)] w-full overflow-hidden rounded-[22px] border border-white/10 bg-ink-900 text-left shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)] transition-[transform,opacity] duration-500 sm:h-[400px] lg:h-[460px] sm:rounded-[28px]",
          active ? "scale-100 opacity-100" : "scale-[0.95] opacity-50")}>
        <img src={preset.cover_url} alt="" className={cn("absolute inset-0 h-full w-full object-cover", active && "animate-ken-burns")} draggable={false} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-transparent" />
        {preset.kind === "video" && (
          <span className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-md"><Play className="h-4 w-4 fill-current" /></span>
        )}
        <div className="absolute inset-x-0 bottom-0 space-y-2 p-5 sm:p-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-300">{eyebrow(preset)}</p>
          <h2 className="text-2xl font-bold uppercase leading-[1.05] tracking-tight text-white sm:text-3xl lg:text-4xl">{preset.title}</h2>
          <p className="line-clamp-2 max-w-xl text-sm leading-relaxed text-zinc-300">{preset.description}</p>
          <span className="inline-flex items-center gap-1.5 pt-1 text-xs font-semibold text-white" data-testid={`carousel-cta-${index}`}>
            {preset.kind === "video" ? "Preview" : "Try this preset"} <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        </div>
      </button>
    </div>
  );
}

function useAutoplay(api, enabled) {
  useEffect(() => {
    if (!api || !enabled) return undefined;
    let paused = false;
    const node = api.rootNode();
    const pause = () => { paused = true; };
    const resume = () => { paused = false; };
    node.addEventListener("pointerenter", pause);
    node.addEventListener("pointerleave", resume);
    api.on("pointerDown", pause);
    const timer = setInterval(() => !paused && api.scrollNext(), 5500);
    return () => {
      clearInterval(timer);
      node.removeEventListener("pointerenter", pause);
      node.removeEventListener("pointerleave", resume);
      api.off("pointerDown", pause);
    };
  }, [api, enabled]);
}

export function FeaturedCarousel({ presets, onOpen }) {
  const calm = useCalmMotion();
  const [ref, api] = useEmblaCarousel({ loop: presets.length > 2, align: "center" });
  const [index, setIndex] = useState(0);
  const onSelect = useCallback(() => api && setIndex(api.selectedScrollSnap()), [api]);
  useEffect(() => {
    if (!api) return undefined;
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => { api.off("select", onSelect); api.off("reInit", onSelect); };
  }, [api, onSelect]);
  useAutoplay(api, !calm && presets.length > 1);
  if (!presets.length) return null;
  return (
    <section aria-roledescription="carousel" aria-label="Featured presets" data-testid="featured-carousel" className="-mx-4 sm:-mx-6">
      <div ref={ref} className="overflow-hidden px-1 sm:px-3">
        <div className="-ml-3 flex touch-pan-y">
          {presets.map((p, i) => <Slide key={p.id} preset={p} index={i} active={i === index} onOpen={onOpen} />)}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-center gap-1.5">
        {presets.map((p, i) => (
          <button key={p.id} type="button" aria-label={`Show slide ${i + 1}`} aria-current={i === index} onClick={() => api?.scrollTo(i)} data-testid={`carousel-dot-${i}`}
            className={cn("h-1.5 rounded-full transition-all duration-300", i === index ? "w-6 bg-white" : "w-1.5 bg-white/30 hover:bg-white/60")} />
        ))}
      </div>
    </section>
  );
}
